import { Badge, Button } from "@design";
import { BookOpen, Lightbulb } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { QuerySection } from "@/components/section";
import { Text } from "@/components/Text";
import { useCapabilities } from "@/features/account/useCurrentUser";
import { inboxPath } from "@/features/knowledge/inbox/inboxPath";
import { useInbox } from "@/features/knowledge/inbox/useInbox";
import { knowledgePath, wikiPagePath } from "@/features/knowledge/KnowledgeTabs";
import { useWikiFiles } from "@/features/knowledge/useWiki";
import { formatRelativeTime } from "@/i18n/format";
import { RailBlock } from "./RailBlock";

const SHOWN = 4;

/** The space's recently updated wiki pages (knowledge graph), when the feature is on. */
export function WikiRailBlock({ spaceUuid }: Readonly<{ spaceUuid: string }>) {
  if (!useCapabilities().knowledgeGraphEnabled) return null;
  return <WikiPages spaceUuid={spaceUuid} />;
}

function WikiPages({ spaceUuid }: Readonly<{ spaceUuid: string }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "detail.wiki" });
  const navigate = useNavigate();
  const wikiFiles = useWikiFiles(spaceUuid);
  const wiki = knowledgePath(spaceUuid, "wiki");
  return (
    <RailBlock
      title={t("title")}
      action={
        <div className="-mr-3">
          <Button variant="text" size="sm" onClick={() => navigate(wiki)}>
            {t("all")}
          </Button>
        </div>
      }
    >
      <WikiSuggestions spaceUuid={spaceUuid} />
      <QuerySection
        query={wikiFiles}
        isEmpty={(wiki) => wiki.files.length === 0}
        empty={{ hint: t("empty") }}
        loadingRows={2}
      >
        {({ files: list }) => (
          <ul className="flex flex-col">
            {[...list]
              .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
              .slice(0, SHOWN)
              .map((file) => (
                <li key={file.path}>
                  <button
                    type="button"
                    onClick={() => navigate(wikiPagePath(spaceUuid, file.path))}
                    className="flex w-full items-center gap-2.5 rounded-md py-1.5 text-left hover:bg-interactive-hover btn-focus-ring"
                  >
                    <BookOpen size={14} aria-hidden className="shrink-0 text-content-tertiary" />
                    <span className="min-w-0 flex-1">
                      <Text as="span" variant="body-sm" className="block truncate">
                        {file.title ?? t("untitled")}
                      </Text>
                      <Text as="span" variant="caption" tone="tertiary" className="block">
                        {formatRelativeTime(file.updatedAt)}
                      </Text>
                    </span>
                  </button>
                </li>
              ))}
          </ul>
        )}
      </QuerySection>
    </RailBlock>
  );
}

/** "N suggestions to review": facts from the user's chats in this space waiting in their inbox. */
function WikiSuggestions({ spaceUuid }: Readonly<{ spaceUuid: string }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "detail.wiki" });
  const navigate = useNavigate();
  const { knowledgeExtractionEnabled } = useCapabilities();
  const inbox = useInbox({ enabled: knowledgeExtractionEnabled });
  const count =
    inbox.data?.proposals.filter((p) => p.status === "pending" && p.chatSpaceUuid === spaceUuid).length ?? 0;
  if (!knowledgeExtractionEnabled || count === 0) return null;
  return (
    <button
      type="button"
      onClick={() => navigate(inboxPath(spaceUuid))}
      className="flex w-full items-center gap-2.5 rounded-md bg-surface-secondary px-2.5 py-2 text-left hover:bg-interactive-hover btn-focus-ring"
    >
      <Lightbulb size={14} aria-hidden className="shrink-0 text-content-secondary" />
      <Text as="span" variant="body-sm" className="min-w-0 flex-1">
        {t("suggestions", { count })}
      </Text>
      <Badge size="compact" variant="emphasis">
        {count}
      </Badge>
    </button>
  );
}
